import {
  ReactElement,
  SyntheticEvent,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useFormikContext } from 'formik';
import {
  Typography,
  Stack,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { ListField, AccessFeature } from '~components/index';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { useToggle } from '~hooks/index';

import {
  FIELDS_NAME,
  getInitialValues,
  SECTIONS_LABELS,
  getFieldLabel,
  getErrorsBySection,
} from '../../utils';
import { OrderFormSectionContainer } from '../../styles';

const OrderFormArticlesSection = (): ReactElement => {
  const { values, submitCount, errors } =
    useFormikContext<ReturnType<typeof getInitialValues>>();

  const [isExpanded, toggleIsExpanded] = useToggle(false);

  const onChangeIsExpanded = useCallback(
    (_event: SyntheticEvent, expanded: boolean) => {
      toggleIsExpanded(expanded);
    },
    [toggleIsExpanded],
  );

  const currentErrorsList = useMemo(
    () => getErrorsBySection(errors, SECTIONS_LABELS.Articles),
    [errors],
  );

  useEffect(() => {
    if (submitCount > 0 && currentErrorsList.length > 0) {
      toggleIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentErrorsList, submitCount]);

  const articles = useMemo(() => values[FIELDS_NAME.ARTICLES], [values]);

  return (
    <OrderFormSectionContainer
      expanded={isExpanded}
      onChange={onChangeIsExpanded}
      disableGutters
      elevation={0}
      variant="outlined"
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-order-form-articles-content"
        id="panel-order-form-articles-header"
      >
        <AccessFeature.Typograghy
          validFeature={PlanFeaturesTypes.MgmItemTask}
          component="h6"
          fontWeight="bold"
        >
          {SECTIONS_LABELS.Articles}
        </AccessFeature.Typograghy>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <AccessFeature.Alert validFeature={PlanFeaturesTypes.MgmItemTask} />

          <AccessFeature.Hidden
            validFeature={PlanFeaturesTypes.MgmItemTask}
            type="notAccess"
          >
            <>
              {!articles.length && (
                <Typography variant="h6" fontWeight="bold" textAlign="center">
                  No hay artículos / tareas cargados
                </Typography>
              )}

              <ListField
                name={FIELDS_NAME.ARTICLES}
                label={getFieldLabel(FIELDS_NAME.ARTICLES)}
                addLabel="Nuevo artículo / tarea"
                addTooltip="Agregar artículo / tarea"
                addItemPosition="last"
                itemLabel="Descripción"
                deleteTooltip="Eliminar artículo / tarea"
                inputsMaxWidth={600}
              />
            </>
          </AccessFeature.Hidden>
        </Stack>
      </AccordionDetails>
    </OrderFormSectionContainer>
  );
};

export default OrderFormArticlesSection;
